import { useEffect, useRef } from "react";

/* Slow flowing motion over the frame's photo ground, ported from the design's
 * "BG — flowing motion" prototype (OPSS-265): four travelling sine waves warp
 * the image along its own folds, a sheen glides over the lit ridges, and a
 * little animated grain keeps the gradients from banding.
 *
 * This paints ON TOP of the CSS background the layout already sets, and only
 * once the texture is decoded — so the still photo is what shows first, and
 * what stays when the motion can't run: no WebGL (or a shader that won't
 * compile), and `prefers-reduced-motion`, which skips the canvas entirely.
 * The loop also parks itself while the tab is hidden.
 *
 * Cover math mirrors `background-size: cover` so the animated frame lines up
 * with the still one underneath it — the swap at load is invisible. */

const VERT = `attribute vec2 p; varying vec2 vUv;
void main(){ vUv = p * 0.5 + 0.5; vUv.y = 1.0 - vUv.y; gl_Position = vec4(p, 0., 1.); }`;

const FRAG = `precision highp float;
varying vec2 vUv;
uniform sampler2D uTex;
uniform float uTime;
uniform vec2 uCover;
uniform vec2 uCoverOff;

void main(){
  vec2 uv = vUv * uCover + uCoverOff;
  float t = uTime;

  // Travelling waves: crests flow steadily along the folds.
  float w1 = sin(uv.x*5.0  + uv.y*2.5 - t*0.40);
  float w2 = sin(uv.x*9.5  - uv.y*3.5 - t*0.26 + 1.7);
  float w3 = sin(uv.y*7.0  + uv.x*1.5 + t*0.32 + 4.0);
  float w4 = sin(uv.x*17.0 + uv.y*11.0 - t*0.50 + 2.3);

  vec2 offset = vec2(
    w2*0.45 + w3*0.55 + w4*0.15,
    w1*0.70 + w2*0.30 + w4*0.12
  ) * 0.018;

  // Inset a hair before sampling so the warp can never reach past the edge.
  vec2 suv = clamp(uv*0.955 + 0.0225 + offset, 0.002, 0.998);
  vec3 col = texture2D(uTex, suv).rgb;

  // Light gliding along the moving ridges, stronger where the image is lit.
  float lum = dot(col, vec3(0.299, 0.587, 0.114));
  float sheen = (w1*0.5 + w3*0.5);
  col += sheen * 0.075 * smoothstep(0.35, 0.75, lum);
  col -= sheen * 0.03 * (1.0 - smoothstep(0.25, 0.6, lum));

  // Animated grain, luma-only, softened in the deep shadows.
  float n = fract(sin(dot(vUv * (1.0 + fract(t)), vec2(12.9898, 78.233)) + t*61.7) * 43758.5453);
  col += (n - 0.5) * 0.045 * (0.55 + 0.45*smoothstep(0.05, 0.35, lum));

  gl_FragColor = vec4(col, 1.0);
}`;

function compile(
  gl: WebGLRenderingContext,
  type: number,
  src: string,
): WebGLShader | null {
  const shader = gl.createShader(type);
  if (!shader) return null;

  gl.shaderSource(shader, src);
  gl.compileShader(shader);
  if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) {
    gl.deleteShader(shader);
    return null;
  }

  return shader;
}

/** Animates `image` over the ground it already paints. Renders nothing at all
 *  when the motion can't (or shouldn't) run. */
export function FlowingBackdrop({ image }: { image: string }) {
  const ref = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = ref.current;
    if (!canvas) return;

    if (window.matchMedia?.("(prefers-reduced-motion: reduce)").matches) return;

    // getContext throws in environments without WebGL (jsdom/tests) — degrade
    // to the still photo rather than crashing the host.
    let gl: WebGLRenderingContext | null = null;
    try {
      gl = canvas.getContext("webgl", {
        antialias: false,
        alpha: true,
      }) as WebGLRenderingContext | null;
    } catch {
      return;
    }
    if (!gl) return;

    const vs = compile(gl, gl.VERTEX_SHADER, VERT);
    const fs = compile(gl, gl.FRAGMENT_SHADER, FRAG);
    if (!vs || !fs) return;

    const prog = gl.createProgram();
    if (!prog) return;

    gl.attachShader(prog, vs);
    gl.attachShader(prog, fs);
    gl.linkProgram(prog);
    if (!gl.getProgramParameter(prog, gl.LINK_STATUS)) return;

    gl.useProgram(prog);

    // One oversized triangle covers the viewport with no index buffer.
    const buf = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, buf);
    gl.bufferData(
      gl.ARRAY_BUFFER,
      new Float32Array([-1, -1, 3, -1, -1, 3]),
      gl.STATIC_DRAW,
    );
    const loc = gl.getAttribLocation(prog, "p");
    gl.enableVertexAttribArray(loc);
    gl.vertexAttribPointer(loc, 2, gl.FLOAT, false, 0, 0);

    const uTime = gl.getUniformLocation(prog, "uTime");
    const uCover = gl.getUniformLocation(prog, "uCover");
    const uCoverOff = gl.getUniformLocation(prog, "uCoverOff");

    let frame = 0;
    let tex: WebGLTexture | null = null;
    let disposed = false;
    let cleanupLoop: (() => void) | null = null;

    const img = new Image();
    img.onload = () => {
      if (disposed || !gl) return;

      tex = gl.createTexture();
      gl.bindTexture(gl.TEXTURE_2D, tex);
      gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGB, gl.RGB, gl.UNSIGNED_BYTE, img);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);

      const resize = () => {
        if (!gl) return;

        // Cap the DPR: this is a full-viewport fragment shader, and past 2x the
        // extra pixels buy nothing on a photograph this soft.
        const dpr = Math.min(window.devicePixelRatio || 1, 2);
        canvas.width = Math.round(canvas.clientWidth * dpr);
        canvas.height = Math.round(canvas.clientHeight * dpr);
        gl.viewport(0, 0, canvas.width, canvas.height);

        // `background: left bottom / cover`, in UV space — the anchor has to
        // match the CSS ground underneath or the canvas jumps when it paints.
        const imgAR = img.width / img.height;
        const boxAR = canvas.width / canvas.height;
        const cx = boxAR > imgAR ? 1 : boxAR / imgAR;
        const cy = boxAR > imgAR ? imgAR / boxAR : 1;
        gl.uniform2f(uCover, cx, cy);
        gl.uniform2f(uCoverOff, 0, 1 - cy);
      };
      resize();
      window.addEventListener("resize", resize);

      const start = performance.now();
      const draw = (now: number) => {
        if (disposed || !gl) return;

        gl.uniform1f(uTime, (now - start) / 1000);
        gl.drawArrays(gl.TRIANGLES, 0, 3);
        frame = requestAnimationFrame(draw);
      };
      frame = requestAnimationFrame(draw);

      // A hidden tab throttles rAF to a crawl anyway; stopping outright keeps
      // it from burning a GPU frame budget nobody is looking at.
      const onVisibility = () => {
        if (document.hidden) {
          cancelAnimationFrame(frame);
          frame = 0;
        } else if (!frame && !disposed) {
          frame = requestAnimationFrame(draw);
        }
      };
      document.addEventListener("visibilitychange", onVisibility);

      cleanupLoop = () => {
        window.removeEventListener("resize", resize);
        document.removeEventListener("visibilitychange", onVisibility);
      };
    };
    img.src = image;

    return () => {
      disposed = true;
      cancelAnimationFrame(frame);
      cleanupLoop?.();
      img.onload = null;
      if (gl) {
        gl.deleteTexture(tex);
        gl.deleteBuffer(buf);
        gl.deleteProgram(prog);
        gl.deleteShader(vs);
        gl.deleteShader(fs);
      }
    };
  }, [image]);

  return (
    <canvas
      ref={ref}
      aria-hidden
      className="pointer-events-none absolute inset-0 h-full w-full"
      style={{ zIndex: 0 }}
    />
  );
}
